import React, { memo } from "react";
import { withNamespaces } from "react-i18next";
import Select from "react-select";
import { Alert, Col, FormGroup, Input, Label, Row } from "reactstrap";
import {
  LEGITIMATION_STATUSES,
  LEGITIMATION_STATUS_CONFIG,
} from "./constants";
import LegitimationStatusBadge from "./LegitimationStatusBadge";

const getSelectOption = (options, value) =>
  options.find((option) => option.value === value) || null;

const getLegitimationStatusSelectStyles = (status) => {
  const backgroundColor = LEGITIMATION_STATUS_CONFIG[status]?.backgroundColor;

  if (!backgroundColor) {
    return undefined;
  }

  return {
    control: (baseStyles) => ({
      ...baseStyles,
      backgroundColor,
      minHeight: "38px",
      "&:hover": {
        backgroundColor,
      },
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      color: "#1D1D1D",
      fontWeight: 600,
    }),
  };
};

const LegitimationSection = ({
  t,
  form,
  isReadOnly,
  onFieldChange,
  formatDateTime,
  notesError,
  variant = "default",
  requesterType,
}) => {
  const status = form.legitimation_status || "pending";
  const isCardVariant = variant === "card";
  const resolvedRequesterType =
    requesterType || form?.requester_type || "company";
  const isPrivatePerson = resolvedRequesterType === "private_person";
  const legitimationStatusOptions = LEGITIMATION_STATUSES.map((value) => ({
    value,
    label: t(LEGITIMATION_STATUS_CONFIG[value].labelKey),
  }));

  const requesterTypeHelper = isPrivatePerson
    ? t(
        "Verify the natural person's identity (email / ID if needed). Do not expect company domain proof."
      )
    : t(
        "Confirm organization affiliation and review any email-domain mismatch on the Request tab."
      );

  const requesterTypeSummary = (
    <Alert color="light" className="mb-3 border">
      <p className="mb-1">
        <strong>{t("Submitting as")}:</strong>{" "}
        {isPrivatePerson ? t("Private person") : t("Company")}
      </p>
      <p className="mb-0 small text-muted">{requesterTypeHelper}</p>
    </Alert>
  );

  if (isCardVariant) {
    return (
      <>
        <h6 className="lia-tab__section-title">{t("Legitimation")}</h6>

        {requesterTypeSummary}

        <FormGroup>
          <Label for="legitimation_status">{t("Status")}</Label>
          <Select
            inputId="legitimation_status"
            classNamePrefix="select2-selection"
            placeholder={`${t("Select")}...`}
            options={legitimationStatusOptions}
            value={getSelectOption(
              legitimationStatusOptions,
              form.legitimation_status
            )}
            isDisabled={isReadOnly}
            menuPortalTarget={document.body}
            styles={getLegitimationStatusSelectStyles(form.legitimation_status)}
            onChange={(option) =>
              onFieldChange("legitimation_status", option?.value || "")
            }
          />
        </FormGroup>

        <FormGroup className="mb-0">
          <Label for="legitimation_notes">
            {t("Notes")}
            {status === "failed" && (
              <span className="text-danger ms-1">*</span>
            )}
          </Label>
          <Input
            type="textarea"
            id="legitimation_notes"
            className="lia-tab__textarea"
            rows="3"
            placeholder={t("Enter notes...")}
            value={form.legitimation_notes || ""}
            disabled={isReadOnly}
            invalid={!!notesError}
            onChange={(e) => onFieldChange("legitimation_notes", e.target.value)}
          />
          {notesError && (
            <div className="invalid-feedback d-block">{notesError}</div>
          )}
        </FormGroup>
      </>
    );
  }

  const statusConfig = LEGITIMATION_STATUS_CONFIG[status];

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0">{t("Legitimation")}</h5>
        <LegitimationStatusBadge status={status} />
      </div>

      {requesterTypeSummary}

      {statusConfig && (
        <Alert color={statusConfig.alertColor} className="mb-3">
          {t(statusConfig.helperKey)}
        </Alert>
      )}

      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="legitimation_status">{t("Legitimation status")}</Label>
            <Select
              inputId="legitimation_status"
              classNamePrefix="select2-selection"
              placeholder={`${t("Select")}...`}
              options={legitimationStatusOptions}
              value={getSelectOption(
                legitimationStatusOptions,
                form.legitimation_status
              )}
              isDisabled={isReadOnly}
              menuPortalTarget={document.body}
              styles={getLegitimationStatusSelectStyles(form.legitimation_status)}
              onChange={(option) =>
                onFieldChange("legitimation_status", option?.value || "")
              }
            />
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>{t("Identity verified on")}</Label>
            <div className="form-control-plaintext">
              {formatDateTime(form.legitimation_verified_at)}
            </div>
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label for="legitimation_notes">
          {t("Legitimation notes")}
          {status === "failed" && (
            <span className="text-danger ms-1">*</span>
          )}
        </Label>
        <Input
          type="textarea"
          id="legitimation_notes"
          rows="3"
          value={form.legitimation_notes || ""}
          disabled={isReadOnly}
          invalid={!!notesError}
          onChange={(e) => onFieldChange("legitimation_notes", e.target.value)}
        />
        {notesError && (
          <div className="invalid-feedback d-block">{notesError}</div>
        )}
      </FormGroup>
    </>
  );
};

export default withNamespaces()(memo(LegitimationSection));
